import {
  useChecklistRuns,
  useDomains,
  useGuides,
  useLogs,
  useLowStock,
  useProjects,
} from '@/lib/queries';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DomainCard } from '@/components/DomainCard';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { currentSeason, currentSeasonLabel, currentYear } from '@houseops/shared';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import LogList from '@/components/LogList';
import { runProgress, runStatusLabel } from '@/lib/checklist-helpers';
import type { ChecklistRun } from '@/lib/types';
import { cn } from '@/lib/utils';

/**
 * Operational dashboard. Reads top-down as: vitals (4 stat cards) →
 * what's live right now (open runs, recent logs) → the field index
 * (domains). The 4 stat cards are the dominant element so the user
 * lands on a *state* view, not a navigation menu.
 *
 * Aesthetic: keep the field-binder treatment. A printed date stamp
 * sits on the right of the hero (lg+ only); each stat card carries a
 * colored index-tab rule on its left edge keyed to the metric's
 * tone (queue & bench in primary, library in muted, inventory glows
 * terracotta when something needs attention).
 */
export default function Dashboard() {
  const domainsQ = useDomains();
  const guidesQ = useGuides();
  const runsQ = useChecklistRuns({});
  const recentLogsQ = useLogs({ limit: 6 });
  const lowStockQ = useLowStock();
  const activeProjectsQ = useProjects({ status: ['planned', 'active', 'blocked'] });
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['domains'] }),
      queryClient.invalidateQueries({ queryKey: ['guides'] }),
      queryClient.invalidateQueries({ queryKey: ['checklist-runs'] }),
      queryClient.invalidateQueries({ queryKey: ['logs'] }),
      queryClient.invalidateQueries({ queryKey: ['consumables'] }),
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
    ]);
  };

  const openRuns = (runsQ.data ?? []).filter(
    (r) => r.status === 'in_progress' || r.status === 'not_started',
  );
  const lowStock = lowStockQ.data ?? [];
  const activeProjects = activeProjectsQ.data ?? [];
  const guides = guidesQ.data ?? [];

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-10 pb-4">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <header
          className="flex flex-col gap-6 border-b border-rule pb-6 md:flex-row md:items-end md:justify-between animate-in fade-in-0 slide-in-from-bottom-1 duration-500"
        >
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Field log <span aria-hidden>·</span> {currentSeasonLabel()}
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
              Dashboard
            </h1>
            <p className="max-w-2xl text-muted-foreground">
              Vitals first. Live work next. The field index lives below — pick a domain to drill in.
            </p>
          </div>
          <div className="hidden md:block">
            <DateStamp />
          </div>
        </header>

        {/* ── Vital stat row ───────────────────────────────────── */}
        <section
          className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4"
          aria-label="Vitals"
        >
          <StatCard
            stamp="Queue"
            title="Open runs"
            value={
              runsQ.data === undefined
                ? '—'
                : openRuns.length === 0
                  ? 'Idle'
                  : String(openRuns.length).padStart(2, '0')
            }
            sub={
              openRuns.length > 0
                ? `Most recent: ${openRuns[0]!.title}`
                : 'No runs in motion. Start one from Checklists.'
            }
            href="/checklists"
            tone="primary"
            valueTestId="open-runs-count"
            delay={0}
          />
          <StatCard
            stamp="Bench"
            title="Active projects"
            value={
              activeProjectsQ.data === undefined
                ? '—'
                : activeProjects.length === 0
                  ? 'Clear'
                  : String(activeProjects.length).padStart(2, '0')
            }
            sub={
              activeProjects.length > 0
                ? `Top: ${activeProjects[0]!.title}`
                : 'No work-in-flight. Bench is clear.'
            }
            href="/projects"
            tone="primary-soft"
            valueTestId="active-projects-count"
            delay={60}
          />
          <StatCard
            stamp="Library"
            title="Guides indexed"
            value={
              guidesQ.data === undefined ? '—' : String(guides.length).padStart(2, '0')
            }
            sub="Markdown bundled into the Worker."
            href="/guides"
            tone="muted"
            valueTestId="guide-count"
            delay={120}
          />
          <StatCard
            stamp="Inventory"
            title="Low stock"
            value={
              lowStockQ.data === undefined
                ? '—'
                : lowStock.length === 0
                  ? 'OK'
                  : String(lowStock.length).padStart(2, '0')
            }
            sub={
              lowStock.length > 0
                ? `Most pressing: ${lowStock[0]!.name}`
                : 'No min-quantity tripwires hit.'
            }
            href="/inventory"
            tone={lowStock.length > 0 ? 'warning' : 'muted'}
            attention={lowStock.length > 0}
            valueTestId="low-stock-count"
            delay={180}
          />
        </section>

        {/* ── Live state: open runs + recent logs ──────────────── */}
        <section
          className="grid gap-6 lg:grid-cols-2 animate-in fade-in-0 slide-in-from-bottom-1 duration-500"
          style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}
          aria-label="Live state"
        >
          <Panel
            title="Open runs"
            stamp="In motion"
            href={openRuns.length > 0 ? '/checklists' : undefined}
            hrefLabel={openRuns.length > 0 ? 'All runs →' : undefined}
          >
            {runsQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : openRuns.length === 0 ? (
              <EmptyPitch
                line="Nothing in motion right now."
                ctaTo="/checklists/templates"
                ctaLabel="Start a run"
              />
            ) : (
              <ul className="divide-y divide-rule rounded-md border border-rule bg-card">
                {openRuns.slice(0, 4).map((r) => (
                  <RunRow key={r.id} run={r} />
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            title="Recent logs"
            stamp="Field memory"
            href={recentLogsQ.data && recentLogsQ.data.length > 0 ? '/logs' : undefined}
            hrefLabel={recentLogsQ.data && recentLogsQ.data.length > 0 ? 'All logs →' : undefined}
          >
            {recentLogsQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : recentLogsQ.data && recentLogsQ.data.length > 0 ? (
              <LogList logs={recentLogsQ.data.slice(0, 4)} />
            ) : (
              <EmptyPitch
                line="No logs yet."
                ctaTo="/logs"
                ctaLabel="Log work"
              />
            )}
          </Panel>
        </section>

        {/* ── Field domains ────────────────────────────────────── */}
        <section
          className="space-y-4 animate-in fade-in-0 slide-in-from-bottom-1 duration-500"
          style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}
          aria-labelledby="domains-heading"
        >
          <div className="flex items-baseline justify-between border-b border-rule pb-2">
            <div className="flex items-baseline gap-3">
              <h2
                id="domains-heading"
                className="font-display text-xl font-semibold tracking-tight"
              >
                Field domains
              </h2>
              {domainsQ.data ? (
                <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground font-tabular">
                  {String(domainsQ.data.length).padStart(2, '0')} indexed
                </span>
              ) : null}
            </div>
            <Link
              to="/domains"
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              Index →
            </Link>
          </div>
          {domainsQ.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
          {domainsQ.isError ? (
            <p className="text-sm text-destructive">{(domainsQ.error as Error).message}</p>
          ) : null}
          {domainsQ.data ? (
            <div
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="domains-grid"
            >
              {domainsQ.data.map((d) => (
                <DomainCard key={d.id} domain={d} />
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </PullToRefresh>
  );
}

// ─────────────────────────────────────────────────────────────────
// Hero date stamp — printed-binder treatment. Day numeral large,
// season + year in mono caps below, double rules above and below
// to read as a stamp impression rather than UI chrome.
// ─────────────────────────────────────────────────────────────────
function DateStamp() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = now.toLocaleString(undefined, { month: 'short' }).toUpperCase();
  const weekday = now.toLocaleString(undefined, { weekday: 'short' }).toUpperCase();
  const season = currentSeason(now).toUpperCase();
  const year = currentYear(now);
  return (
    <div
      aria-hidden
      className="select-none border-y border-rule px-4 py-2 text-right font-mono leading-tight"
    >
      <div className="flex items-baseline justify-end gap-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {weekday}
        </span>
        <span className="font-display text-3xl font-semibold tabular-nums tracking-tight text-foreground">
          {day}
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {month}
        </span>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
        {season} <span className="text-rule">·</span> {year}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Stat card — tonally accented, card-shaped, big-number-first. The
// left rule is a 4px index-tab keyed to the metric's tone:
//   primary       → forest, the dominant green (active queue / runs)
//   primary-soft  → forest at 30% (steady-state work)
//   muted         → neutral hairline (informational)
//   warning       → terracotta (tripwire — needs attention)
// An attention dot floats top-right when terracotta is on.
// ─────────────────────────────────────────────────────────────────
type StatTone = 'primary' | 'primary-soft' | 'muted' | 'warning';

function StatCard({
  stamp,
  title,
  value,
  sub,
  href,
  tone,
  attention,
  valueTestId,
  delay = 0,
}: {
  stamp: string;
  title: string;
  value: string;
  sub?: string;
  href?: string;
  tone: StatTone;
  attention?: boolean;
  valueTestId?: string;
  delay?: number;
}) {
  const ruleClass =
    tone === 'primary'
      ? 'bg-primary'
      : tone === 'primary-soft'
        ? 'bg-primary/30'
        : tone === 'warning'
          ? 'bg-warning'
          : 'bg-rule';

  const inner = (
    <Card
      className={cn(
        'relative h-full overflow-hidden transition-all duration-200',
        'group-hover:-translate-y-0.5 group-hover:shadow-md',
        tone === 'warning' &&
          'border-warning/40 bg-warning/[0.04] group-hover:border-warning/60',
        tone === 'primary' && 'group-hover:border-primary/30',
      )}
    >
      {/* Index-tab rule */}
      <div className={cn('absolute inset-y-0 left-0 w-1', ruleClass)} aria-hidden />
      {/* Attention dot */}
      {attention ? (
        <span
          aria-hidden
          className="absolute right-3 top-3 flex h-2.5 w-2.5"
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning/60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-warning" />
        </span>
      ) : null}
      <CardHeader className="space-y-1 pb-2 pl-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {stamp}
        </p>
        <CardTitle className="font-display text-sm font-medium text-foreground/80">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-0 pl-5 pb-5">
        <p
          className={cn(
            'font-display text-4xl font-semibold leading-none tracking-tight font-tabular',
            tone === 'warning' ? 'text-warning' : 'text-foreground',
          )}
          data-testid={valueTestId}
        >
          {value}
        </p>
        {sub ? (
          <CardDescription className="line-clamp-2 pt-2">{sub}</CardDescription>
        ) : null}
      </CardContent>
    </Card>
  );

  const wrapped = href ? (
    <Link
      to={href}
      className="group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {inner}
    </Link>
  ) : (
    <div className="group">{inner}</div>
  );

  return (
    <div
      className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}
    >
      {wrapped}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Live-state panel — wraps a section with a stamped header and an
// optional "All X →" link.
// ─────────────────────────────────────────────────────────────────
function Panel({
  title,
  stamp,
  href,
  hrefLabel,
  children,
}: {
  title: string;
  stamp: string;
  href?: string;
  hrefLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between border-b border-rule pb-2">
        <div className="flex items-baseline gap-3">
          <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {stamp}
          </span>
        </div>
        {href && hrefLabel ? (
          <Link
            to={href}
            className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            {hrefLabel}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Compact run row — title, run-status stamp, slim progress bar.
// ─────────────────────────────────────────────────────────────────
function RunRow({ run }: { run: ChecklistRun }) {
  const pct = runProgress(run);
  return (
    <li>
      <Link
        to={`/checklists/runs/${run.id}`}
        className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-accent/40 transition-colors"
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {runStatusLabel(run)}
          </p>
          <p className="truncate font-display text-base leading-tight">{run.title}</p>
          <div className="flex items-center gap-2">
            <div className="h-1 w-full max-w-[160px] overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-[width] duration-300"
                style={{ width: pct === null ? '0%' : `${pct}%` }}
              />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums font-tabular">
              {pct === null ? '—' : `${pct}%`}
            </span>
          </div>
        </div>
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
      </Link>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────
// Tiny calm-state pitch for empty panels.
// ─────────────────────────────────────────────────────────────────
function EmptyPitch({
  line,
  ctaTo,
  ctaLabel,
}: {
  line: string;
  ctaTo: string;
  ctaLabel: string;
}) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-md border border-dashed border-rule bg-card/50 px-4 py-6">
      <p className="text-sm text-muted-foreground">{line}</p>
      <Link
        to={ctaTo}
        className="inline-flex items-center gap-1.5 rounded-md border border-rule bg-card px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-foreground transition-colors hover:border-primary/40 hover:text-primary"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> {ctaLabel}
      </Link>
    </div>
  );
}
